/**
 * Copyright (c) 2025 Adaptive Chaos Labs, LLC
 * All rights reserved.
 */

/**
 * **The artifact's authoring plane** — what an operator or a template states *about* a document,
 * which the runtime never reads (`TM4`/`TM10`, `#1170`).
 *
 * ## The gap it closes
 *
 * A template has to be able to say **what it does not know**: the publisher id, account id, tagid
 * or deal id that only the operator has. `D32`'s `from: 'operator'` is exactly that declaration,
 * and before this it reached **one destination of one type** — `DemandSourceParamField.suppliedBy`,
 * a query-parameter row.
 *
 * An ORTB body leaf could not say it at all. A `MappingLeaf` is `static | macro | expression`, so an
 * unfilled leaf is an empty string on the wire rather than a declaration — and the body is the
 * destination OpenRTB actually uses (measured on the pre-pilot deployment: **287 of 386 authored
 * body leaves are literal constants**, publisher and routing values). A template that wanted to say
 * *"Magnite needs `app.publisher.id` and only you have it"* had to invent a value or omit the leaf
 * and let the operator find out from a no-bid.
 *
 * The gap spans two ratified recommendations without either noticing: `R5a` **produces** this
 * classification, `R6` **consumes** it as the coverage claim, and no artifact field stored it.
 *
 * ## Why a side-car, and why a column
 *
 * - **Not in the document.** Some documents are sent verbatim — the ORTB body is — so an attribute
 *   written into one goes on the wire. `OrtbBodyTemplateDto.provenance` and `headersProvenance`
 *   already exist for that reason; this is the same shape one level up.
 * - **Not on a type's DTO.** It is a property of **templating**, not of demand sources, and `TM-D`
 *   puts it on every templatable type. `A8`'s rule is that a concept two types share belongs on the
 *   base — and the base's cross-cutting attributes are columns, which is what the `artifacts` table
 *   exists to hold.
 * - **A column, ratified** (Todd, 2026-08-26): *"isn't the simplest path to add a column to the
 *   artifact store? We can afford to change the db now."*
 *
 * **It does not pass `A8`'s column bar, and that is deliberate rather than overlooked.** That bar is
 * *the storage layer must read it without opening the payload* — a join, an index, a SQL filter —
 * and nothing queries this. It earns the column on a second test `A8` does not cover and `TM10`
 * states: **a storage split by plane.** If a lean runtime read is ever built, a column gives it *by
 * construction*, where an enumerated *"runtime needs these fields"* filter would be
 * `AdServerPreset`/`isUnchanged` one layer down. Measured: ~17% of stored artifact bytes on the live
 * backup are authoring-only.
 *
 * ## Named for the plane, not the feature
 *
 * `TM10`. The column is `authoring`, not `template_coverage`, and {@link ArtifactAuthoringDto} is a
 * record whose **first key** is coverage. A feature-named column makes the second occupant a
 * *change* rather than an *addition*, which is exactly the corollary's test.
 */

import type { DestinationValue } from './destination-field';
import { catalogEntryFor } from './field-catalog';

/**
 * **Where in an artifact a declaration applies** — the slot address.
 *
 * `<plane>:<address>`, and the separator is `:` because no plane name contains one while every
 * address form does contain the characters that would otherwise be ambiguous: a body path is
 * dotted (`app.publisher.id`), a header name is hyphenated (`x-vendor-key`), a query parameter is
 * whatever the vendor calls it.
 *
 * The plane vocabulary is **per artifact type** — a demand source has an endpoint, parameters,
 * headers and a request body; a pixel has a URL; a strategy has referenced ids. `TM7` says matching
 * is per-type and `TM8` puts per-type policy on `A1`'s descriptor, which does not exist yet, so the
 * demand-source planes are declared in {@link DEMAND_SOURCE_SLOT_PLANES} here rather than branched
 * on at each call site. When the descriptor lands this moves onto it as an *addition*.
 */
export type AuthoringSlot = string;

/**
 * The planes a demand source addresses. One list, so a new one is added rather than assumed.
 *
 * **`header` joined in `4b-ii` (`#1354`), in the same change as the control that writes it** — the
 * headers editor's *Required*. It was held back until then because declaring a plane nothing writes is
 * `D20`'s third leg failing — *something writes the path it reads* — which is `{schain}`'s defect.
 * A header's address is its name as authored.
 */
export const DEMAND_SOURCE_SLOT_PLANES = Object.freeze(['endpoint', 'param', 'header', 'body'] as const);

export type DemandSourceSlotPlane = (typeof DEMAND_SOURCE_SLOT_PLANES)[number];

/**
 * Address one slot.
 *
 * The endpoint takes no address — there is one of it — so `slotFor('endpoint')` is the bare plane
 * name. Everything else is `plane:address`.
 */
export function slotFor(plane: DemandSourceSlotPlane, address?: string): AuthoringSlot {
  return address === undefined || address === '' ? plane : `${plane}:${address}`;
}

/** Split a slot back into its plane and address. Returns `null` for a plane nothing declares. */
export function parseSlot(slot: AuthoringSlot): { plane: DemandSourceSlotPlane; address?: string } | null {
  const separator = slot.indexOf(':');
  const plane = separator < 0 ? slot : slot.slice(0, separator);
  if (!(DEMAND_SOURCE_SLOT_PLANES as readonly string[]).includes(plane)) return null;
  return separator < 0
    ? { plane: plane as DemandSourceSlotPlane }
    : { plane: plane as DemandSourceSlotPlane, address: slot.slice(separator + 1) };
}

/**
 * What the artifact states about its own authoring.
 *
 * One key today. It is an object rather than the coverage map itself for the reason above: the
 * second occupant must be an addition.
 */
export interface ArtifactAuthoringDto {
  /**
   * **What each slot's value comes from**, where the document cannot say it — keyed by
   * {@link AuthoringSlot}.
   *
   * The vocabulary is `D32`'s and **does not grow privately** — the same prohibition `D31` put on
   * template `domains` and `D35` on `when`. The whole of it is declared, though only `operator` is
   * written today: `derived` and `declared` are already readable *from the document* (a macro row is
   * derived, a static value is declared), and writing them here would give one fact two homes.
   *
   * Declaring the unwritten members anyway is the `DI1` precedent — *declare the whole vocabulary,
   * not just what is built*, so a consumer can tell the cases apart without knowing which one we
   * happen to emit. {@link isRedundantCoverage} is what keeps the rule checkable.
   */
  coverage?: Record<AuthoringSlot, DestinationValue>;
}

/**
 * **Coverage a document already states, and which therefore must not be stored here.**
 *
 * The one-home rule, made checkable rather than left as a comment. `derived` is a macro row or a
 * `$expr` leaf; `declared` is a static value sitting in the document. Storing either would create
 * the second home this side-car exists to avoid, and the two would then be free to disagree.
 */
export function isRedundantCoverage(value: DestinationValue): boolean {
  return value.from === 'derived' || value.from === 'declared';
}

/**
 * **A shape with a hole in it, not a URL** — the endpoint plane's declaration, derived (`#955`).
 *
 * `https://tv.springserve.com/vast/{supplyTagId}` parses as a perfectly valid URL, so URL
 * validation alone waves it through — and the source would then be tested, and no-bid, for a reason
 * that has nothing to do with its configuration.
 *
 * **One implementation, and that is the point** (`#1170`). This was written twice — once in
 * `libs/adapters/ad-server-presets.ts` and once in the console's wizard helpers — because the
 * catalogue it belonged to could not express the class it was standing in for (`destination-field.ts`
 * says so in its own header). With `D32`'s `operator` case declarable per slot, it stops being a
 * bespoke validator and becomes the one place the **endpoint** plane's claim is derived: an
 * endpoint's placeholders are visible in its value, where a parameter's or a body leaf's are not,
 * so that plane is derived and the other two are declared.
 */
export function endpointHasPlaceholder(endpoint: string | undefined): boolean {
  return unfilledEndpointPlaceholders(endpoint).length > 0;
}

/**
 * **The holes in an endpoint: every `{name}` the macro vocabulary does not resolve** (`#955`,
 * corrected in `1f-ii`).
 *
 * This read *any* `{…}` as a hole, so `https://ssp.example.com/vast?ip={ip}` or `…&ad={max_duration}`
 * was an unfinished endpoint: the wizard told the operator to replace a macro the driver fills on
 * every request, and the source counted as not ready. Its own spec was titled *does not mistake a
 * query-string macro for an unfilled placeholder* and asserted that it did. Found rehearsing `1f-ii`,
 * whose endpoint was the first to carry a brace macro. A name the catalog knows is resolved at request
 * time; a name it does not (`{supplyTagId}`) is the vendor's value the operator still has to supply.
 * Brackets are never a hole: `[TIMESTAMP]` is IAB syntax the driver resolves or leaves as written.
 */
export function unfilledEndpointPlaceholders(endpoint: string | undefined): string[] {
  const names = [...(endpoint ?? '').matchAll(/\{([A-Za-z0-9_]+)\}/g)].map((m) => m[1]);
  return names.filter((name) => catalogEntryFor(name) === undefined).map((name) => `{${name}}`);
}

/** What a template says about one slot, or nothing. */
export function coverageFor(
  authoring: ArtifactAuthoringDto | undefined | null,
  slot: AuthoringSlot,
): DestinationValue | undefined {
  return authoring?.coverage?.[slot];
}

/**
 * Every slot the artifact declares **only the operator can fill**.
 *
 * Still `R6`'s coverage claim and still **read** rather than guessed at from the shape of a string —
 * what changed on 2026-08-27 is where the claim is written. It was a checkbox of its own; it is now
 * derived from `required` + unfilled, so the operator answers one question instead of two that mean
 * nearly the same thing. The union below is what makes that a widening rather than a swap: a
 * declaration a **vendor preset** states directly still counts, and eleven of those were stated
 * rather than inferred on the same day precisely so this stays a read.
 */
export function operatorSuppliedSlots(authoring: ArtifactAuthoringDto | undefined | null): AuthoringSlot[] {
  return Object.entries(authoring?.coverage ?? {})
    .filter(([, value]) => value?.from === 'operator')
    .map(([slot]) => slot);
}
